import React from 'react';
import styles from './ScoreCard.module.css';

/**
 * ScoreCard — score-with-progress-bar card.
 *
 * Lifted out of VulnerabilityDetail.jsx during Phase 7 so the
 * card's chrome (background, border, radius, typography) lives in
 * a token-driven CSS module instead of inline rgba styles. The
 * progress bar's `width` + `background` are still inline because
 * they're data-driven — the rest is class-driven.
 *
 * Props:
 *   label    — human-readable label ("CVSS Base Score", ...).
 *   value    — raw numeric value (or null/undefined for N/A).
 *   maxValue — 10 for CVSS, 1 for EPSS. Drives both the display
 *              format and the percentage fill.
 *   colorFn  — (value) => hex. Picks the bar fill color from the
 *              shared severity util.
 */

function formatValue(value, maxValue) {
  if (value === null || value === undefined) return 'N/A';
  if (maxValue === 1.0) return value.toString();
  return value.toFixed(1);
}

function percentageOf(value, maxValue) {
  if (value === null || value === undefined) return 0;
  return Math.min(100, Math.max(0, (value / maxValue) * 100));
}

const ScoreCard = ({ label, value, maxValue = 10.0, colorFn }) => (
  <div className={styles.card}>
    <div className={styles.head}>
      <span className={styles.label}>{label}</span>
      <span className={styles.value}>{formatValue(value, maxValue)}</span>
    </div>
    <div className={styles.bar}>
      <div
        className={styles.barFill}
        style={{ width: `${percentageOf(value, maxValue)}%`, background: colorFn(value) }}
      />
    </div>
  </div>
);

export default ScoreCard;
